import {
  type BlockNoteEditor,
  type PartialBlock,
} from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { useCreateBlockNote } from "@blocknote/react";
import { ToastService } from "@shared/lib/services";
import classNames from "classnames";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { BriefExportResult } from "../lib/briefExport";
import {
  stripCommentMarksFromHtml,
  whenEditorViewReady,
  type EditorCommentsConfig,
} from "../lib/editorComments";
import { editorSchema } from "../lib/editorSchema";
import { editorDocumentHasContent } from "../lib/editorBlocks";
import { bindEditorFileGuards } from "../lib/editorFileDrop";
import { isImageFile } from "../lib/editorFileTypes";
import {
  hasLocalMediaInEditor,
  isMediaUploadInFlight,
  processAndUploadMediaBlocks,
  uploadEditorFileToFirebase,
  waitForEditorMediaIdle,
} from "../lib/editorMedia";
import {
  downloadBriefAsDoc,
  downloadBriefAsPdf,
  editorDocumentToHtml,
  importBriefFromFile,
} from "../lib/editorOperations";
import {
  createEditorCommentsExtension,
  useEditorComments,
} from "../lib/useEditorComments";
import styles from "./styles.module.scss";

const emptyContent: PartialBlock[] = [{ type: "paragraph", content: "" }];

/** Helpers are typed against the default schema; cast at this boundary. */
const asDefaultEditor = (editor: object) => editor as BlockNoteEditor;

export type EditorHandle = {
  importFile: (file: File) => Promise<void>;
  downloadDoc: (campaignName: string) => Promise<BriefExportResult>;
  downloadPdf: (campaignName: string) => Promise<BriefExportResult>;
  /** `syncTokens: false` serializes without mutating image blocks (autosave dirty check). */
  getHTML: (options?: { syncTokens?: boolean }) => string;
  hasContent: () => boolean;
  startPendingComment: () => void;
  stopPendingComment: () => void;
  selectThread: (threadId: string | undefined, quote?: string) => void;
  getThreadQuote: (threadId: string) => string | null;
  applyCommentMark: (threadId: string, quote?: string) => void;
  restoreCommentMarks: (
    anchors: Array<{ threadId: string; quote: string }>
  ) => void;
};

interface EditorProps {
  initialHtml?: string;
  editable?: boolean;
  compact?: boolean;
  className?: string;
  onChange?: () => void;
  /** Pause brief autosave while media uploads to Firebase */
  onMediaUploadStart?: () => void;
  /** Save formV2 only after Firebase media URLs are on the blocks */
  onMediaUploadComplete?: () => void;
  onReady?: () => void;
  comments?: EditorCommentsConfig;
  /** Import dropped/pasted .doc/.docx/.pdf (images uploaded, video discarded). */
  allowDocumentImport?: boolean;
}

const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      initialHtml = "",
      editable = true,
      compact = false,
      className,
      onChange,
      onMediaUploadStart,
      onMediaUploadComplete,
      onReady,
      comments,
      allowDocumentImport = false,
    },
    ref
  ) => {
    const hasHydratedRef = useRef(false);
    const isHydratingRef = useRef(true);
    const isProcessingMediaRef = useRef(false);
    const pendingMediaSaveRef = useRef(false);
    const isImportingRef = useRef(false);
    const editorRootRef = useRef<HTMLDivElement>(null);
    const onChangeRef = useRef(onChange);
    const onMediaUploadStartRef = useRef(onMediaUploadStart);
    const onMediaUploadCompleteRef = useRef(onMediaUploadComplete);
    const onReadyRef = useRef(onReady);

    onChangeRef.current = onChange;
    onMediaUploadStartRef.current = onMediaUploadStart;
    onMediaUploadCompleteRef.current = onMediaUploadComplete;
    onReadyRef.current = onReady;

    const editor = useCreateBlockNote({
      schema: editorSchema,
      initialContent: emptyContent,
      extensions: comments
        ? [createEditorCommentsExtension(comments)]
        : undefined,
      uploadFile: async (file: File) => {
        if (!isImageFile(file)) {
          ToastService.error("Only image uploads are supported");
          throw new Error("Only image uploads are supported");
        }
        pendingMediaSaveRef.current = true;
        onMediaUploadStartRef.current?.();
        try {
          const remoteUrl = await uploadEditorFileToFirebase(file);
          // BlockNote writes the URL after this resolves; onChange can be skipped
          // if another upload is in flight. Flush once the queue is idle.
          queueMicrotask(() => {
            if (isProcessingMediaRef.current || isMediaUploadInFlight()) return;
            pendingMediaSaveRef.current = false;
            onMediaUploadCompleteRef.current?.();
            onChangeRef.current?.();
          });
          return remoteUrl;
        } catch (error) {
          pendingMediaSaveRef.current = false;
          onMediaUploadCompleteRef.current?.();
          onChangeRef.current?.();
          throw error;
        }
      },
      placeholders: {
        default: "Type '/' for commands, or just start writing…",
        heading: "Heading",
        bulletListItem: "List",
        numberedListItem: "List",
      },
    });

    const runDocumentImport = async (file: File) => {
      isImportingRef.current = true;
      onMediaUploadStartRef.current?.();
      try {
        await importBriefFromFile(asDefaultEditor(editor), file);
        await waitForEditorMediaIdle();
      } finally {
        pendingMediaSaveRef.current = false;
        onMediaUploadCompleteRef.current?.();
        onChangeRef.current?.();
        queueMicrotask(() => {
          isImportingRef.current = false;
          onMediaUploadCompleteRef.current?.();
          onChangeRef.current?.();
        });
      }
    };

    // BlockNote defaults unmatched drops (e.g. video) to a `file` block. With
    // file/video removed from the schema, that crashes — reject first.
    useEffect(() => {
      const root = editorRootRef.current;
      if (!root) return;

      return bindEditorFileGuards(root, {
        allowDocumentImport,
        importDocument: async (file: File) => {
          try {
            await runDocumentImport(file);
          } catch (error) {
            ToastService.error(
              error instanceof Error ? error.message : "Could not import file"
            );
          }
        },
      });
    }, [allowDocumentImport, editor]);

    useEffect(() => {
      let cancelled = false;
      isHydratingRef.current = true;

      const applyInitialHtml = () => {
        if (cancelled) return;

        if (!hasHydratedRef.current) {
          hasHydratedRef.current = true;
          const html = stripCommentMarksFromHtml(initialHtml.trim());
          if (html) {
            try {
              const blocks = editor.tryParseHTMLToBlocks(html);
              if (blocks.length > 0) {
                editor.replaceBlocks(editor.document, blocks);
              }
            } catch {
              // Keep empty editor if HTML cannot be parsed
            }
          }
        }

        whenEditorViewReady(asDefaultEditor(editor), () => {
          if (cancelled) return;
          isHydratingRef.current = false;
          onReadyRef.current?.();
        });
      };

      const startHydrate = () => {
        whenEditorViewReady(asDefaultEditor(editor), applyInitialHtml);
      };

      const unsubMount = editor.onMount(startHydrate);
      if (!editor.headless) startHydrate();

      return () => {
        cancelled = true;
        unsubMount();
      };
    }, [editor, initialHtml]);

    const commentActions = useEditorComments(
      asDefaultEditor(editor),
      comments
    );

    useImperativeHandle(ref, () => {
      const e = asDefaultEditor(editor);
      return {
        importFile: (file: File) => runDocumentImport(file),
        downloadDoc: (campaignName: string) =>
          downloadBriefAsDoc(e, campaignName),
        downloadPdf: (campaignName: string) =>
          downloadBriefAsPdf(e, campaignName),
        getHTML: (options) => editorDocumentToHtml(e, options),
        hasContent: () => editorDocumentHasContent(e.document),
        startPendingComment: commentActions.startPendingComment,
        stopPendingComment: commentActions.stopPendingComment,
        selectThread: commentActions.selectThread,
        getThreadQuote: commentActions.getThreadQuote,
        applyCommentMark: commentActions.applyCommentMark,
        restoreCommentMarks: commentActions.restoreCommentMarks,
      };
    }, [commentActions, editor]);

    const notifyContentChange = () => {
      onChangeRef.current?.();
    };

    const notifyMediaComplete = () => {
      pendingMediaSaveRef.current = false;
      onMediaUploadCompleteRef.current?.();
      notifyContentChange();
    };

    const handleEditorChange = async () => {
      if (!hasHydratedRef.current || isHydratingRef.current) return;
      commentActions.syncPendingAnchor();

      // Import already uploads images and emits start/complete. Ignore the
      // follow-up BlockNote onChange so Attach is not left in a busy state.
      if (isImportingRef.current) {
        notifyContentChange();
        return;
      }

      // Keep parent hasContent/dirty in sync even while media is uploading.
      // Autosave callers pause on onMediaUploadStart, so this will not persist blob URLs.
      if (isProcessingMediaRef.current) {
        notifyContentChange();
        return;
      }

      // BlockNote uploadFile still in flight — pause autosave, wait for next onChange
      if (isMediaUploadInFlight()) {
        pendingMediaSaveRef.current = true;
        onMediaUploadStartRef.current?.();
        notifyContentChange();
        return;
      }

      const defaultEditor = asDefaultEditor(editor);

      // Local blob/data media still on blocks — upload to Firebase first
      if (hasLocalMediaInEditor(defaultEditor)) {
        pendingMediaSaveRef.current = true;
        onMediaUploadStartRef.current?.();
        isProcessingMediaRef.current = true;
        notifyContentChange();
        try {
          await processAndUploadMediaBlocks(defaultEditor);
          notifyMediaComplete();
        } catch {
          ToastService.error("Failed to upload media. Please try again.");
          notifyMediaComplete();
        } finally {
          isProcessingMediaRef.current = false;
        }
        return;
      }

      // Firebase upload finished and URLs are on blocks — save formV2 now
      if (pendingMediaSaveRef.current) {
        notifyMediaComplete();
        return;
      }

      // Normal text/structure edits — debounced autosave
      notifyContentChange();
    };

    return (
      <div
        className={classNames(
          styles.editorRoot,
          compact && styles.editorRootCompact,
          className
        )}
        ref={editorRootRef}
      >
        <BlockNoteView
          editor={editor}
          theme="light"
          editable={editable}
          autoFocus={false}
          comments={false}
          sideMenu={!compact}
          onChange={handleEditorChange}
        />
      </div>
    );
  }
);

Editor.displayName = "Editor";

export { Editor };
export default Editor;
